/* ──────────────────────────────────────────────────────────────────────────
 * Canadian Prime rate loader — the single read path for historical rates.
 *
 * Companion to ticker-loader.ts. Same architectural shape: async function
 * returning a snapshot, reading from a committed JSON cache today, swappable
 * to runtime fetch (Vercel Blob) tomorrow without touching anything else.
 *
 * Source: Bank of Canada Valet series V80691311 — chartered bank prime
 * rate, published weekly. Refreshed manually via `npm run refresh-rates`.
 *
 * Prime is a step function (it only changes when BoC moves the policy
 * rate, ~8 times per year). Weekly observations forward-filled to daily
 * is the faithful model. The `primeRateAt(series, date)` helper does that
 * lookup — see its doc comment for the exact semantics.
 * ────────────────────────────────────────────────────────────────────────── */

import primeData from "./canadian-prime.json";

// ── Types ──────────────────────────────────────────────────────────────────

export type PrimeRateObservation = {
  /** ISO YYYY-MM-DD date of the observation (BoC publishes Wednesdays). */
  readonly date: string;
  /** Prime rate as a decimal (e.g., 0.0445 = 4.45%). */
  readonly rate: number;
};

export type PrimeRateMeta = {
  readonly source: string;
  readonly seriesId: string;
  readonly seriesLabel: string;
  readonly firstDate: string;
  readonly lastDate: string;
  readonly observationCount: number;
  readonly generatedAt: string;
};

export type PrimeRateSeries = {
  readonly meta: PrimeRateMeta;
  readonly observations: readonly PrimeRateObservation[];
};

// ── Loader ─────────────────────────────────────────────────────────────────

/**
 * Load the full Canadian Prime rate series. The return value is read-only
 * by convention.
 */
export async function loadPrimeRates(): Promise<PrimeRateSeries> {
  return primeData as PrimeRateSeries;
}

// ── Lookup helper ──────────────────────────────────────────────────────────

/**
 * Forward-fill lookup: return the rate from the most recent observation
 * whose date is ≤ targetDate.
 *
 * Why forward-fill: Prime is a step function. Between BoC announcements
 * (typically 8/year), the rate is constant. Weekly observations capture
 * every change; intermediate days simply carry the most recent rate.
 * Daily interpolation would model a smooth glide that doesn't exist.
 *
 * If targetDate is after the last observation, returns the last rate
 * (Prime carries forward indefinitely until BoC moves again).
 *
 * Throws if targetDate is before the first observation — the simulator
 * shouldn't be running scenarios that pre-date the available data.
 */
export function primeRateAt(series: PrimeRateSeries, targetDate: string): number {
  const obs = series.observations;
  if (obs.length === 0) {
    throw new Error("primeRateAt: empty observation series");
  }
  if (targetDate < obs[0]!.date) {
    throw new Error(`primeRateAt: date ${targetDate} is before first observation ${obs[0]!.date}`);
  }

  // Binary search for the largest index where obs[idx].date ≤ targetDate.
  let lo = 0;
  let hi = obs.length - 1;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (obs[mid]!.date <= targetDate) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return obs[lo]!.rate;
}
